import { useEffect, useState, type FormEvent } from "react";
import { AlertTriangle, CheckCircle2 } from "lucide-react";
import { Button } from "@/shared/components/Button";
import { BotaoVoltar } from "@/shared/components/BotaoVoltar";
import { SeletorMinisterios } from "@/shared/components/SeletorMinisterios";
import {
  chaveDia,
  combinarDataHora,
  dataHojeInput,
  formatarHora,
  respeitaAntecedenciaMinima,
  separarDataHora,
} from "@/shared/lib/datas";
import type { Ambiente } from "@/modules/reservas/services/ambientesService";
import {
  reservasService,
  type ConflitoAmbiente,
  type DadosReserva,
  type ReservaComRelacoes,
} from "@/modules/reservas/services/reservasService";

const CLASSE_INPUT =
  "w-full rounded-lg border border-border bg-surface-2 px-3 py-2.5 text-sm text-text placeholder:text-muted focus:border-primary focus:outline-none focus:ring-2 focus:ring-primary/30";
const CLASSE_LABEL = "text-sm font-medium text-text";

export interface MinisterioOpcao {
  id: string;
  nome: string;
}

interface FormularioReservaProps {
  valoresIniciais?: ReservaComRelacoes | null;
  ambientes: Ambiente[];
  ministerios: MinisterioOpcao[];
  carregando: boolean;
  erro?: string | null;
  onSubmit: (dados: DadosReserva) => void;
}

export function FormularioReserva({
  valoresIniciais,
  ambientes,
  ministerios,
  carregando,
  erro,
  onSubmit,
}: FormularioReservaProps) {
  const iniInicio = valoresIniciais ? separarDataHora(valoresIniciais.inicio) : null;
  const iniFim = valoresIniciais ? separarDataHora(valoresIniciais.fim) : null;

  const [ambienteId, setAmbienteId] = useState(valoresIniciais?.ambiente_id ?? "");
  const [titulo, setTitulo] = useState(valoresIniciais?.titulo ?? "");
  const [data, setData] = useState(iniInicio?.data ?? "");
  const [horaInicio, setHoraInicio] = useState(iniInicio?.hora ?? "");
  const [horaFim, setHoraFim] = useState(iniFim?.hora ?? "");
  // F2-32: agora suporta mais de um ministério vinculado (atividade conjunta).
  const [ministerioIds, setMinisterioIds] = useState<string[]>(
    valoresIniciais?.ministerios?.map((m) => m.id) ??
      (valoresIniciais?.ministerio_id ? [valoresIniciais.ministerio_id] : []),
  );
  const [observacoes, setObservacoes] = useState(valoresIniciais?.observacoes ?? "");

  const [conflitos, setConflitos] = useState<ConflitoAmbiente[]>([]);
  const [checando, setChecando] = useState(false);

  const completo = !!(ambienteId && data && horaInicio && horaFim);
  const intervaloValido = completo && horaFim > horaInicio;
  const minData = dataHojeInput();
  const inicioParaChecar = intervaloValido ? combinarDataHora(data, horaInicio) : null;
  const antecedenciaOk = !inicioParaChecar || respeitaAntecedenciaMinima(inicioParaChecar);

  useEffect(() => {
    if (!intervaloValido) {
      setConflitos([]);
      return;
    }
    let cancelado = false;
    setChecando(true);
    const inicioISO = combinarDataHora(data, horaInicio);
    const fimISO = combinarDataHora(data, horaFim);
    reservasService
      .verificarConflitos(ambienteId, inicioISO, fimISO, valoresIniciais?.id ?? null)
      .then((res) => {
        if (!cancelado) setConflitos(res);
      })
      .catch(() => {
        if (!cancelado) setConflitos([]);
      })
      .finally(() => {
        if (!cancelado) setChecando(false);
      });
    return () => {
      cancelado = true;
    };
  }, [ambienteId, data, horaInicio, horaFim, intervaloValido, valoresIniciais?.id]);

  const temConflito = conflitos.length > 0;
  const primeiroConflito = conflitos[0];

  // F4-17: em vez de só bloquear, sugere o próximo horário livre do mesmo
  // ambiente no mesmo dia (logo após o fim do conflito mais tardio),
  // preservando a duração original escolhida. Se não couber mais no dia,
  // não sugere nada — a pessoa escolhe outro dia/ambiente.
  function proximoHorarioLivre(): { inicio: string; fim: string } | null {
    if (!temConflito || !intervaloValido) return null;
    const duracaoMs =
      new Date(combinarDataHora(data, horaFim)).getTime() -
      new Date(combinarDataHora(data, horaInicio)).getTime();
    if (duracaoMs <= 0) return null;
    const fimMaisTardio = conflitos.reduce(
      (max, c) => (new Date(c.fim) > max ? new Date(c.fim) : max),
      new Date(0),
    );
    const propostaFim = new Date(fimMaisTardio.getTime() + duracaoMs);
    if (chaveDia(fimMaisTardio.toISOString()) !== data || chaveDia(propostaFim.toISOString()) !== data) {
      return null;
    }
    return { inicio: formatarHora(fimMaisTardio.toISOString()), fim: formatarHora(propostaFim.toISOString()) };
  }
  const sugestaoHorario = proximoHorarioLivre();

  function usarSugestao() {
    if (!sugestaoHorario) return;
    setHoraInicio(sugestaoHorario.inicio);
    setHoraFim(sugestaoHorario.fim);
  }

  function submit(e: FormEvent) {
    e.preventDefault();
    if (!intervaloValido || temConflito || !antecedenciaOk) return;
    onSubmit({
      ambienteId,
      titulo: titulo.trim(),
      inicio: combinarDataHora(data, horaInicio),
      fim: combinarDataHora(data, horaFim),
      ministerioIds,
      observacoes: observacoes.trim() || null,
    });
  }

  return (
    <form onSubmit={submit} className="flex flex-col gap-4" noValidate>
      <div className="flex flex-col gap-1.5">
        <label htmlFor="reserva-ambiente" className={CLASSE_LABEL}>
          Ambiente
        </label>
        <select
          id="reserva-ambiente"
          value={ambienteId}
          onChange={(e) => setAmbienteId(e.target.value)}
          className={CLASSE_INPUT}
          required
        >
          <option value="" disabled>
            Selecione um ambiente
          </option>
          {ambientes.map((a) => (
            <option key={a.id} value={a.id}>
              {a.nome}
            </option>
          ))}
        </select>
      </div>

      <div className="flex flex-col gap-1.5">
        <label htmlFor="reserva-titulo" className={CLASSE_LABEL}>
          Finalidade
        </label>
        <input
          id="reserva-titulo"
          value={titulo}
          onChange={(e) => setTitulo(e.target.value)}
          placeholder="Ex.: Ensaio do Louvor"
          className={CLASSE_INPUT}
          required
        />
      </div>

      <div className="flex flex-col gap-1.5">
        <label htmlFor="reserva-data" className={CLASSE_LABEL}>
          Data
        </label>
        <input
          id="reserva-data"
          type="date"
          value={data}
          min={minData}
          onChange={(e) => setData(e.target.value)}
          className={CLASSE_INPUT}
          required
        />
      </div>

      <div className="flex gap-3">
        <div className="flex flex-1 flex-col gap-1.5">
          <label htmlFor="reserva-inicio" className={CLASSE_LABEL}>
            Início
          </label>
          <input
            id="reserva-inicio"
            type="time"
            value={horaInicio}
            onChange={(e) => setHoraInicio(e.target.value)}
            className={CLASSE_INPUT}
            required
          />
        </div>
        <div className="flex flex-1 flex-col gap-1.5">
          <label htmlFor="reserva-fim" className={CLASSE_LABEL}>
            Término
          </label>
          <input
            id="reserva-fim"
            type="time"
            value={horaFim}
            onChange={(e) => setHoraFim(e.target.value)}
            className={CLASSE_INPUT}
            required
          />
        </div>
      </div>

      {completo && !intervaloValido && (
        <p role="alert" aria-live="polite" className="text-xs font-medium text-warning">
          O horário de término deve ser depois do início.
        </p>
      )}

      {intervaloValido && !antecedenciaOk && (
        <p role="alert" aria-live="polite" className="text-xs font-medium text-danger">
          Não é possível reservar em data/horário que já passou. Para hoje, agende com pelo menos 1h
          de antecedência.
        </p>
      )}

      <div className="flex flex-col gap-1.5">
        <span className={CLASSE_LABEL}>
          Ministério <span className="text-muted">(opcional · pode marcar mais de um)</span>
        </span>
        {/* F2-40 (protótipo aprovado 24-25/07/2026): antes era uma lista
            fixa de checkboxes numa caixinha com scroll — trocado por busca ao
            vivo + chips removíveis (SeletorMinisterios). A lista já vem
            restrita pelo chamador — quem não gerencia o módulo só vê os
            ministérios que lidera. */}
        <SeletorMinisterios
          id="reserva-ministerios"
          ministerios={ministerios}
          selecionados={ministerioIds}
          onChange={setMinisterioIds}
        />
      </div>

      <div className="flex flex-col gap-1.5">
        <label htmlFor="reserva-obs" className={CLASSE_LABEL}>
          Observações <span className="text-muted">(opcional)</span>
        </label>
        <textarea
          id="reserva-obs"
          value={observacoes}
          onChange={(e) => setObservacoes(e.target.value)}
          rows={2}
          placeholder="Detalhes da reserva…"
          className={CLASSE_INPUT}
        />
      </div>

      {/* Aviso de conflito ao vivo (a prioridade de evento público vem do banco). */}
      {intervaloValido && !checando && primeiroConflito && (
        <div
          role="alert"
          aria-live="assertive"
          className="flex items-start gap-2 rounded-lg bg-danger/10 px-3 py-2.5 text-sm text-danger"
        >
          <AlertTriangle size={18} className="mt-0.5 shrink-0" />
          <div>
            <b>Ambiente ocupado neste horário.</b> Já existe reserva das{" "}
            {formatarHora(primeiroConflito.inicio)} às {formatarHora(primeiroConflito.fim)}
            {conflitos.some((c) => c.ehPublico) && " (evento público, com prioridade)"}. Escolha
            outro horário ou ambiente.
            {/* F4-17: sugestão de horário alternativo em vez de só bloquear. */}
            {sugestaoHorario ? (
              <button
                type="button"
                onClick={usarSugestao}
                className="mt-2 block text-xs font-semibold text-danger underline hover:no-underline"
              >
                Usar próximo horário livre: {sugestaoHorario.inicio}–{sugestaoHorario.fim}
              </button>
            ) : (
              <p role="alert" aria-live="polite" className="mt-1 text-xs text-danger/80">
                Não há mais horário livre para esse ambiente neste dia — tente outro dia ou
                ambiente.
              </p>
            )}
          </div>
        </div>
      )}
      {intervaloValido && !checando && !temConflito && (
        <div className="flex items-center gap-2 rounded-lg bg-success/10 px-3 py-2.5 text-sm text-success">
          <CheckCircle2 size={18} className="shrink-0" />
          Ambiente livre neste horário.
        </div>
      )}

      {erro && (
        <p role="alert" className="rounded-md bg-danger/10 px-3 py-2 text-sm text-danger">
          {erro}
        </p>
      )}

      <Button
        type="submit"
        carregando={carregando}
        disabled={!intervaloValido || temConflito || !antecedenciaOk}
        className="mt-1"
      >
        {temConflito
          ? "Resolva o conflito para salvar"
          : valoresIniciais
            ? "Salvar alterações"
            : "Criar reserva"}
      </Button>
      <BotaoVoltar disabled={carregando} className="-mt-2" />
    </form>
  );
}
